import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, CircleDashed, LogIn } from 'lucide-react';
import type { TrophyParticipation } from '../../types/trophy';

interface TrophyParticipationBannerProps {
  participation: TrophyParticipation;
  signedIn: boolean;
}

/**
 * One line under the circuit's header answering the question a climber
 * opens the page with: am I in? In: how many stages of how many. Not in: say
 * so, and point at the buttons below. A visitor is pointed at signing in,
 * because the answer depends on who is asking.
 */
export default function TrophyParticipationBanner({
  participation,
  signedIn,
}: TrophyParticipationBannerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const anyOpen = participation.stages.some((stage) => stage.open);

  if (!signedIn) {
    if (!anyOpen) return null;
    return (
      <p
        className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm"
        data-testid="trophy-participation"
        data-state="visitor"
      >
        <LogIn className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span>
          {t('stageEntry.summaryVisitor')}{' '}
          <Link to="/sign-in" state={{ from: location.pathname }} className="font-medium text-primary hover:underline">
            {t('auth.signIn')}
          </Link>
        </span>
      </p>
    );
  }

  if (participation.entered) {
    return (
      <p
        className="flex items-center gap-2 rounded-md border border-green-600/40 bg-green-600/10 px-3 py-2 text-sm text-green-700 dark:text-green-400"
        data-testid="trophy-participation"
        data-state="entered"
      >
        <CheckCircle2 className="size-4 shrink-0" aria-hidden />
        {t('stageEntry.summaryEntered', {
          count: participation.entries_count,
          total: participation.stages.length,
        })}
      </p>
    );
  }

  return (
    <p
      className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm"
      data-testid="trophy-participation"
      data-state="not-entered"
    >
      <CircleDashed className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span>
        {t('stageEntry.summaryNotEntered')}
        {anyOpen && ` ${t('stageEntry.summaryHint')}`}
      </span>
    </p>
  );
}
